import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, NO_PERMISSION_MESSAGE } from '../context/AuthContext';
import { adminPath } from '../utils/adminPaths';

const ROLE_LABELS = { admin: 'Administrator', editor: 'Editor', viewer: 'Viewer' };
const roleLabel = (role) => {
  const r = String(role || '').toLowerCase();
  return ROLE_LABELS[r] || (r ? r.charAt(0).toUpperCase() + r.slice(1) : 'Unknown');
};

// Shown instead of empty pages when the signed-in account has no admin permission
// (non-admin role, or the API answered 403).
export const AccessDenied = ({ message, user, onLogout, onRetry }) => (
  <div
    className="admin-access-denied"
    dir="ltr"
    lang="en"
    role="alert"
    style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', background: '#f4f6f8' }}
  >
    <div style={{ maxWidth: 480, width: '100%', background: '#fff', borderRadius: 12, padding: '32px 28px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'center' }}>
      <i className="la la-lock" style={{ fontSize: 48, color: '#e74c3c' }}></i>
      <h2 style={{ margin: '12px 0 8px', fontSize: 22, color: '#333' }}>Access denied</h2>
      <p style={{ margin: '0 0 8px', color: '#555' }}>{message || NO_PERMISSION_MESSAGE}</p>
      {user && (
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#888' }}>
          Signed in as <strong>{user.username}</strong> ({roleLabel(user.role)}). An Administrator account is required.
        </p>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        {onRetry && (
          <button type="button" className="btn-secondary" onClick={onRetry} style={{ padding: '10px 18px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>
            Try again
          </button>
        )}
        <button type="button" className="btn-primary" onClick={onLogout} style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: '#30766b', color: '#fff', cursor: 'pointer' }}>
          Sign in with another account
        </button>
      </div>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, isAdmin, forbidden, clearForbidden, logout } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={adminPath('/login')} replace />;
  }

  if (!isAdmin || forbidden) {
    return (
      <AccessDenied
        message={forbidden?.message}
        user={user}
        onLogout={logout}
        onRetry={forbidden && isAdmin ? clearForbidden : undefined}
      />
    );
  }

  return children;
};

export default ProtectedRoute;
